import React from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  useTheme,
  IconButton,
  Chip,
} from "@mui/material";
import {
  DesktopWindows,
  PhoneIphone,
  TabletMac,
  Close,
  CheckCircle,
  Videocam,
  Visibility,
  Mic,
  Dashboard,
} from "@mui/icons-material";
import withStyles from '@mui/styles/withStyles';

const styles = (theme) => ({
  dialogPaper: {
    maxWidth: 520,
    margin: theme.spacing(2),
    borderRadius: theme.spacing(3),
    background: theme.palette.mode === 'dark' 
      ? 'linear-gradient(135deg, rgba(30, 30, 35, 0.95) 0%, rgba(40, 40, 50, 0.98) 100%)'
      : 'linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(248, 250, 252, 0.95) 100%)',
    backdropFilter: 'blur(20px)',
    boxShadow: theme.palette.mode === 'dark'
      ? '0 25px 50px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.05)'
      : '0 25px 50px rgba(148, 97, 255, 0.15), 0 0 0 1px rgba(148, 97, 255, 0.08)',
    overflow: 'visible',
  },
  header: {
    position: 'relative',
    textAlign: 'center',
    padding: theme.spacing(4, 3, 2),
    background: 'transparent',
  },
  closeButton: {
    position: 'absolute',
    right: theme.spacing(1.5),
    top: theme.spacing(1.5),
    color: theme.palette.text.secondary,
    backgroundColor: theme.palette.mode === 'dark' 
      ? 'rgba(255, 255, 255, 0.08)'
      : 'rgba(0, 0, 0, 0.04)',
    borderRadius: '10px',
    transition: 'all 0.3s ease',
    '&:hover': {
      backgroundColor: theme.palette.mode === 'dark' 
        ? 'rgba(255, 255, 255, 0.12)'
        : 'rgba(0, 0, 0, 0.08)',
      color: theme.palette.primary.main,
      transform: 'scale(1.05)',
    },
  },
  iconContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: theme.spacing(3),
    position: 'relative',
    height: '120px',
  },
  // Enhanced desktop icon with animation
  desktopIconWrapper: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100px',
    height: '100px',
    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark || theme.palette.primary.main})`,
    borderRadius: '20px',
    boxShadow: `0 15px 35px rgba(148, 97, 255, 0.4)`,
    animation: '$glow 3s ease-in-out infinite alternate, $float 4s ease-in-out infinite',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: '-3px',
      left: '-3px',
      right: '-3px',
      bottom: '-3px',
      background: `linear-gradient(135deg, ${theme.palette.primary.main}, transparent, ${theme.palette.primary.main})`,
      borderRadius: '23px',
      opacity: 0.3,
      animation: '$rotate 4s linear infinite',
      zIndex: -1,
    },
  },
  desktopIcon: {
    fontSize: 48,
    color: 'white',
    filter: 'drop-shadow(0 2px 8px rgba(0, 0, 0, 0.2))',
  },
  mobileIconWrapper: {
    position: 'absolute',
    right: '10px',
    bottom: '10px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '40px',
    height: '40px',
    backgroundColor: theme.palette.mode === 'dark' 
      ? 'rgba(255, 255, 255, 0.1)'
      : 'rgba(0, 0, 0, 0.05)',
    borderRadius: '10px',
    animation: '$bounce 2s ease-in-out infinite',
  },
  mobileIcon: {
    fontSize: 24,
    color: theme.palette.text.secondary,
    opacity: 0.7,
  },
  title: {
    fontWeight: 700,
    fontSize: '1.75rem',
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(1),
    fontFamily: theme.typography.fontFamily,
  },
  titleEmoji: {
    fontSize: '1.5rem',
    marginRight: theme.spacing(1),
    fontFamily: 'Apple Color Emoji, Segoe UI Emoji, Noto Color Emoji, sans-serif',
  },
  titleText: {
    background: theme.palette.mode === 'dark' 
      ? 'linear-gradient(135deg, #ffffff, #e0e0e0)'
      : `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark || theme.palette.primary.main})`,
    backgroundClip: 'text',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    display: 'inline',
  },
  subtitle: {
    fontSize: '1rem',
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(3),
    fontWeight: 500,
  },
  content: {
    padding: theme.spacing(0, 3, 2),
  },
  featureGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(3),
  },
  featureCard: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: theme.spacing(2),
    backgroundColor: theme.palette.mode === 'dark' 
      ? 'rgba(255, 255, 255, 0.05)'
      : 'rgba(148, 97, 255, 0.04)',
    borderRadius: '12px',
    border: theme.palette.mode === 'dark'
      ? '1px solid rgba(255, 255, 255, 0.08)'
      : '1px solid rgba(148, 97, 255, 0.1)',
    transition: 'all 0.3s ease',
    '&:hover': {
      transform: 'translateY(-2px)',
      backgroundColor: theme.palette.mode === 'dark' 
        ? 'rgba(255, 255, 255, 0.08)'
        : 'rgba(148, 97, 255, 0.08)',
    },
  },
  featureIcon: {
    fontSize: 24,
    color: theme.palette.primary.main,
    marginBottom: theme.spacing(1),
  },
  featureText: {
    fontSize: '0.875rem',
    fontWeight: 600,
    color: theme.palette.text.primary,
    textAlign: 'center',
    lineHeight: 1.3,
  },
  featureSubtext: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    textAlign: 'center',
    marginTop: theme.spacing(0.5),
  },
  recommendationBadge: {
    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark || theme.palette.primary.main})`,
    color: 'white',
    fontWeight: 600,
    fontSize: '0.75rem',
    padding: theme.spacing(0.5, 1.5),
    borderRadius: '20px',
    marginBottom: theme.spacing(2),
    boxShadow: `0 4px 12px rgba(148, 97, 255, 0.3)`,
  },
  actions: {
    padding: theme.spacing(2, 3, 3),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
  },
  primaryButton: {
    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark || theme.palette.primary.main})`,
    color: 'white',
    fontWeight: 600,
    fontSize: '1rem',
    padding: theme.spacing(1.5, 3),
    borderRadius: '12px',
    textTransform: 'none',
    boxShadow: `0 8px 25px rgba(148, 97, 255, 0.3)`,
    transition: 'all 0.3s ease',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: `0 12px 35px rgba(148, 97, 255, 0.4)`,
      background: `linear-gradient(135deg, ${theme.palette.primary.dark || theme.palette.primary.main}, ${theme.palette.primary.main})`,
    },
  },
  secondaryButton: {
    color: theme.palette.text.secondary,
    fontWeight: 500,
    fontSize: '0.9rem',
    padding: theme.spacing(1, 2),
    borderRadius: '10px',
    textTransform: 'none',
    backgroundColor: 'transparent',
    border: theme.palette.mode === 'dark'
      ? '1px solid rgba(255, 255, 255, 0.1)'
      : '1px solid rgba(0, 0, 0, 0.1)',
    transition: 'all 0.3s ease',
    '&:hover': {
      backgroundColor: theme.palette.mode === 'dark' 
        ? 'rgba(255, 255, 255, 0.05)'
        : 'rgba(0, 0, 0, 0.02)',
      borderColor: theme.palette.primary.main,
      color: theme.palette.primary.main,
    },
  },
  deviceInfo: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    textAlign: 'center',
    marginTop: theme.spacing(1),
    opacity: 0.7,
  },
  
  // Animations
  '@keyframes glow': {
    '0%': {
      boxShadow: `0 15px 35px rgba(148, 97, 255, 0.4)`,
    },
    '100%': {
      boxShadow: `0 20px 45px rgba(148, 97, 255, 0.6)`,
    },
  },
  '@keyframes float': {
    '0%, 100%': {
      transform: 'translateY(0px)',
    },
    '50%': {
      transform: 'translateY(-8px)',
    },
  },
  '@keyframes rotate': {
    '0%': {
      transform: 'rotate(0deg)',
    },
    '100%': {
      transform: 'rotate(360deg)',
    },
  },
  '@keyframes bounce': {
    '0%, 20%, 50%, 80%, 100%': {
      transform: 'translateY(0)',
    },
    '40%': {
      transform: 'translateY(-5px)',
    },
    '60%': {
      transform: 'translateY(-3px)',
    },
  },
});

function MobileDetectionModal({ classes, open, onClose, onContinue, deviceInfo, blocking = true }) {
  const theme = useTheme();
  const isTablet = deviceInfo?.deviceType === 'tablet';
  
  const getDeviceIcon = () => {
    switch (deviceInfo?.deviceType) {
      case 'tablet':
        return <TabletMac className={classes.mobileIcon} />;
      case 'mobile':
        return <PhoneIphone className={classes.mobileIcon} />;
      default:
        return <PhoneIphone className={classes.mobileIcon} />;
    }
  };

  const getTitle = () => {
    if (isTablet) {
      return { emoji: "✨", text: "Desktop Experience Recommended" };
    }
    return { emoji: "🚀", text: "Desktop Required for Best Experience" };
  };

  const getSubtitle = () => {
    if (isTablet) {
      return "Get the full power of Auralyze's AI coaching on desktop";
    }
    return "Unlock Auralyze's complete AI interview coaching suite";
  };

  const features = [
    { icon: Videocam, title: "HD Recording", subtitle: "Crystal clear video for AI analysis" },
    { icon: Visibility, title: "Eye Tracking", subtitle: "Advanced gaze & body language detection" },
    { icon: Mic, title: "Speech Analysis", subtitle: "Superior audio quality & processing" },
    { icon: Dashboard, title: "Full Dashboard", subtitle: "Complete analytics & insights" },
  ];

  const handleDesktopRedirect = () => {
    // Redirect to homepage
    window.location.href = '/';
  };

  return (
    <Dialog
      open={open}
      onClose={blocking ? undefined : onClose}
      disableEscapeKeyDown={blocking}
      classes={{
        paper: classes.dialogPaper,
      }}
      maxWidth="sm"
    >
      <div className={classes.header}>
        {!blocking && (
          <IconButton
            className={classes.closeButton}
            onClick={onClose}
            size="small"
          >
            <Close />
          </IconButton>
        )}
        
        <div className={classes.iconContainer}>
          <div className={classes.desktopIconWrapper}>
            <DesktopWindows className={classes.desktopIcon} />
          </div>
          <div className={classes.mobileIconWrapper}>
            {getDeviceIcon()}
          </div>
        </div>

        <Chip 
          label={isTablet ? "Recommended" : "Required"} 
          className={classes.recommendationBadge}
          size="small"
        />

        <Typography variant="h5" className={classes.title}>
          <span className={classes.titleEmoji}>{getTitle().emoji}</span>
          <span className={classes.titleText}>{getTitle().text}</span>
        </Typography>
        
        <Typography variant="body1" className={classes.subtitle}>
          {getSubtitle()}
        </Typography>
      </div>

      <DialogContent className={classes.content}>
        <div className={classes.featureGrid}>
          {features.map((feature, index) => (
            <div key={index} className={classes.featureCard}>
              <feature.icon className={classes.featureIcon} />
              <Typography className={classes.featureText}>
                {feature.title}
              </Typography>
              <Typography className={classes.featureSubtext}>
                {feature.subtitle}
              </Typography>
            </div>
          ))}
        </div>

        {deviceInfo && (
          <Typography className={classes.deviceInfo}>
            Detected: {deviceInfo.deviceType} • {deviceInfo.screenWidth}×{deviceInfo.screenHeight}
          </Typography>
        )}
      </DialogContent>

      <DialogActions className={classes.actions}>
        <Button
          variant="contained"
          className={classes.primaryButton}
          onClick={handleDesktopRedirect}
          fullWidth
          disableElevation
        >
          {isTablet ? "Switch to Desktop" : "Use Desktop Instead"}
        </Button>
        
        {!blocking && (
          <Button
            className={classes.secondaryButton}
            onClick={onContinue}
            fullWidth
          >
            Continue anyway
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

MobileDetectionModal.propTypes = {
  classes: PropTypes.object.isRequired,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func,
  onContinue: PropTypes.func,
  deviceInfo: PropTypes.object,
  blocking: PropTypes.bool,
};

export default withStyles(styles)(MobileDetectionModal); 